import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import ExpandCollapseTransition from "@tSurya/Components/ExpandCollapseTransition";
import {
    BrandGroupAmounts,
    combineGroups,
    getPaymentBreakdown,
    getSplitTotals,
    isTermsPaymentMethod,
    PaymentSplitChoice,
} from "@tSurya/Common/Utilities/paymentBreakdown";
import { COLORS, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import React, { useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

const ENTIRE_ORDER_LABEL = "Entire Order";
const MGBW_LABEL = "Mitchell Gold + Bob Williams";
const OTHER_ITEMS_LABEL = "All Other Items";
const checkoutPaymentInformationTokens = (WIDGETS as any).checkoutPaymentInformation;
// The Mobius withDisabler/withTheme HOC drops children from its inferred public type.
const PaymentRadio = Radio as unknown as React.FC<React.PropsWithChildren<RadioProps>>;

interface SurPaymentBreakdownProps {
    cart: Cart | undefined;
    userHasTerms: boolean;
    /** The bill-to has an MGBW term code: the whole order is terms-eligible (terms-for-all). */
    userHasMgbwTerms?: boolean;
    shippingPromotionTotal?: number;
    selectedChoice: PaymentSplitChoice;
    onChoiceChange: (choice: PaymentSplitChoice) => void;
}

const Money: React.FC<{ amount: number; currencySymbol?: string }> = ({ amount, currencySymbol }) => (
    <LocalizedCurrency amount={amount} currencySymbol={currencySymbol} />
);

/** Full-width white card that mirrors the checkout "Payment" section box. */
const SectionBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <StyledWrapper
        css={css`
            display: block;
            width: 100%;
            background-color: ${COLORS.white};
            border: 1px solid ${COLORS.border.main};
            border-radius: ${checkoutPaymentInformationTokens.paymentBreakdown.boxBorderRadius};
            padding: 20px 24px;
        `}
    >
        {children}
    </StyledWrapper>
);

interface PaymentAllocationSummary {
    kind: "chargedToday" | "onTerms";
    heading: string;
    tag: string;
    label?: string;
    amount: number;
}

interface PaymentDetailGroup {
    kind: "chargedToday" | "onTerms";
    heading: string;
    tag: string;
    label?: string;
    amounts: BrandGroupAmounts;
    showTotal: boolean;
    totalLabel: string;
}

interface PaymentOptionView {
    title: string;
    subtitle: string;
    headlineAmount: number;
    summaries: PaymentAllocationSummary[];
    detailGroups: PaymentDetailGroup[];
    finalTotalLabel?: string;
    finalTotal?: number;
}

const CompactAllocationRow: React.FC<{
    summary: PaymentAllocationSummary;
    currencySymbol?: string;
}> = ({ summary, currencySymbol }) => {
    const paymentBreakdownTokens = checkoutPaymentInformationTokens.paymentBreakdown;
    const accent =
        summary.kind === "chargedToday"
            ? paymentBreakdownTokens.chargedTodayColor
            : paymentBreakdownTokens.onTermsColor;

    return (
        <StyledWrapper
            css={css`
                display: flex;
                align-items: flex-start;
                flex-direction: column;
                justify-content: space-between;
                gap: ${paymentBreakdownTokens.compactRowGap};
                padding: ${paymentBreakdownTokens.compactRowMobilePadding};
                background-color: ${paymentBreakdownTokens.compactRowBackground};
                border-left: ${paymentBreakdownTokens.compactRowAccentWidth} solid ${accent};
                border-radius: ${paymentBreakdownTokens.boxBorderRadius};

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            align-items: center;
                            flex-direction: row;
                            padding: ${paymentBreakdownTokens.compactRowPadding};
                        `,
                        null,
                        null,
                        null,
                    ])}
            `}
        >
            <StyledWrapper css={css`min-width: 0;`}>
                <Typography
                    as="p"
                    css={css`
                        margin: 0;
                        color: ${accent};
                        font-size: ${paymentBreakdownTokens.compactMetaFontSize};
                        font-weight: ${paymentBreakdownTokens.compactMetaFontWeight};
                        letter-spacing: ${paymentBreakdownTokens.compactMetaLetterSpacing};
                        line-height: ${paymentBreakdownTokens.compactMetaLineHeight};
                        text-transform: uppercase;
                    `}
                >
                    {translate(summary.heading)} · {translate(summary.tag)}
                </Typography>
                {summary.label && (
                    <Typography
                        as="p"
                        css={css`
                            margin: ${paymentBreakdownTokens.compactLabelMargin};
                            color: ${COLORS.checkout.text.secondary};
                            font-size: ${paymentBreakdownTokens.compactLabelFontSize};
                            line-height: ${paymentBreakdownTokens.compactLabelLineHeight};
                        `}
                    >
                        {summary.label}
                    </Typography>
                )}
            </StyledWrapper>
            <Typography
                css={css`
                    color: ${accent};
                    font-size: ${paymentBreakdownTokens.compactAmountFontSize};
                    font-weight: ${paymentBreakdownTokens.compactAmountFontWeight};
                    line-height: ${paymentBreakdownTokens.compactAmountLineHeight};
                    text-align: right;
                    white-space: nowrap;
                    align-self: flex-end;

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            css`
                                align-self: auto;
                            `,
                            null,
                            null,
                            null,
                        ])}
                `}
            >
                <Money amount={summary.amount} currencySymbol={currencySymbol} />
            </Typography>
        </StyledWrapper>
    );
};

const DetailSection: React.FC<{
    group: PaymentDetailGroup;
    currencySymbol?: string;
    showWhiteGlove: boolean;
    showShippingPromotion: boolean;
}> = ({ group, currencySymbol, showWhiteGlove, showShippingPromotion }) => {
    const paymentBreakdownTokens = checkoutPaymentInformationTokens.paymentBreakdown;
    const accent =
        group.kind === "chargedToday"
            ? paymentBreakdownTokens.chargedTodayColor
            : paymentBreakdownTokens.onTermsColor;
    const rows = [
        { key: "subtotal", label: "Subtotal", value: group.amounts.subtotal, isDiscount: false },
        ...(group.amounts.rollPadService > 0
            ? [
                  {
                      key: "rollPadService",
                      label: "Roll Pad Service",
                      value: group.amounts.rollPadService,
                      isDiscount: false,
                  },
              ]
            : []),
        { key: "shipping", label: "Shipping & Handling", value: group.amounts.freight, isDiscount: false },
        ...(showWhiteGlove
            ? [
                  {
                      key: "whiteGlove",
                      label: "White Glove",
                      value: group.amounts.whiteGlove,
                      isDiscount: false,
                  },
              ]
            : []),
        ...(showShippingPromotion
            ? [
                  {
                      key: "shippingPromotion",
                      label: "Shipping Promotion",
                      value: group.amounts.shippingDiscount,
                      isDiscount: true,
                  },
              ]
            : []),
        { key: "tax", label: "Tax", value: group.amounts.tax, isDiscount: false },
    ];

    return (
        <StyledWrapper
            css={css`
                flex: 1 1 ${paymentBreakdownTokens.detailSectionMinWidth};
                display: flex;
                flex-direction: column;
                min-width: 0;
                border: ${paymentBreakdownTokens.detailBorder};
                border-radius: ${paymentBreakdownTokens.boxBorderRadius};
                overflow: hidden;
            `}
        >
            <StyledWrapper
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: ${paymentBreakdownTokens.detailHeaderGap};
                    padding: ${paymentBreakdownTokens.detailHeaderPadding};
                    background-color: ${paymentBreakdownTokens.detailHeaderBackground};
                    border-left: ${paymentBreakdownTokens.detailHeaderAccentWidth} solid ${accent};
                `}
            >
                <Typography
                    css={css`
                        color: ${accent};
                        font-weight: ${paymentBreakdownTokens.detailHeaderFontWeight};
                    `}
                >
                    {translate(group.heading)}
                </Typography>
                <Typography
                    css={css`
                        color: ${accent};
                        font-size: ${paymentBreakdownTokens.detailTagFontSize};
                        font-weight: ${paymentBreakdownTokens.detailTagFontWeight};
                        letter-spacing: ${paymentBreakdownTokens.detailTagLetterSpacing};
                        text-align: right;
                        text-transform: uppercase;
                    `}
                >
                    {translate(group.tag)}
                </Typography>
            </StyledWrapper>
            <StyledWrapper
                css={css`
                    display: flex;
                    flex: 1;
                    flex-direction: column;
                    padding: ${paymentBreakdownTokens.detailBodyPadding};
                `}
            >
                {group.label && (
                    <Typography
                        as="p"
                        css={css`
                            color: ${COLORS.checkout.text.secondary};
                            font-size: ${paymentBreakdownTokens.detailLabelFontSize};
                            letter-spacing: ${paymentBreakdownTokens.detailLabelLetterSpacing};
                            margin-bottom: ${paymentBreakdownTokens.detailLabelMarginBottom};
                            text-transform: uppercase;
                        `}
                    >
                        {group.label}
                    </Typography>
                )}
                {rows.map(row => (
                    <StyledWrapper
                        key={row.key}
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            gap: ${paymentBreakdownTokens.detailRowGap};
                            color: ${COLORS.checkout.text.secondary};
                            margin-bottom: ${paymentBreakdownTokens.detailRowMarginBottom};
                        `}
                    >
                        <Typography>{translate(row.label)}</Typography>
                        <Typography
                            css={css`
                                text-align: right;
                                white-space: nowrap;
                            `}
                        >
                            {row.isDiscount && row.value > 0 ? "-" : ""}
                            <Money amount={row.value} currencySymbol={currencySymbol} />
                        </Typography>
                    </StyledWrapper>
                ))}
                {group.showTotal && (
                    <StyledWrapper
                        css={css`
                            display: flex;
                            justify-content: space-between;
                            gap: ${paymentBreakdownTokens.detailRowGap};
                            margin-top: auto;
                            padding-top: ${paymentBreakdownTokens.detailTotalPaddingTop};
                            border-top: ${paymentBreakdownTokens.detailTotalBorder};
                        `}
                    >
                        <Typography
                            css={css`
                                color: ${COLORS.checkout.text.primary};
                                font-weight: ${paymentBreakdownTokens.detailTotalFontWeight};
                            `}
                        >
                            {translate(group.totalLabel)}
                        </Typography>
                        <Typography
                            css={css`
                                color: ${accent};
                                font-weight: ${paymentBreakdownTokens.detailTotalFontWeight};
                                text-align: right;
                                white-space: nowrap;
                            `}
                        >
                            <Money amount={group.amounts.total} currencySymbol={currencySymbol} />
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const DetailedBreakdown: React.FC<{
    groups: PaymentDetailGroup[];
    finalTotalLabel?: string;
    finalTotal?: number;
    currencySymbol?: string;
}> = ({ groups, finalTotalLabel, finalTotal, currencySymbol }) => {
    const paymentBreakdownTokens = checkoutPaymentInformationTokens.paymentBreakdown;
    const showWhiteGlove = groups.some(group => group.amounts.whiteGlove > 0);
    const showShippingPromotion = groups.some(group => group.amounts.shippingDiscount > 0);

    return (
        <StyledWrapper css={css`padding-top: ${paymentBreakdownTokens.expandedContentPaddingTop};`}>
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-wrap: wrap;
                    gap: ${paymentBreakdownTokens.detailSectionGap};
                `}
            >
                {groups.map((group, index) => (
                    <DetailSection
                        key={`${group.heading}-${group.label || "entire-order"}-${index}`}
                        group={group}
                        currencySymbol={currencySymbol}
                        showWhiteGlove={showWhiteGlove}
                        showShippingPromotion={showShippingPromotion}
                    />
                ))}
            </StyledWrapper>
            {finalTotalLabel && finalTotal !== undefined && (
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: baseline;
                        justify-content: space-between;
                        gap: ${paymentBreakdownTokens.finalTotalGap};
                        margin-top: ${paymentBreakdownTokens.finalTotalMarginTop};
                        padding-top: ${paymentBreakdownTokens.finalTotalPaddingTop};
                        border-top: ${paymentBreakdownTokens.finalTotalBorder};
                    `}
                >
                    <Typography
                        css={css`
                            color: ${COLORS.checkout.text.primary};
                            font-weight: ${paymentBreakdownTokens.finalTotalFontWeight};
                        `}
                    >
                        {translate(finalTotalLabel)}
                    </Typography>
                    <Typography
                        css={css`
                            color: ${COLORS.checkout.text.primary};
                            font-size: ${paymentBreakdownTokens.finalTotalAmountFontSize};
                            font-weight: ${paymentBreakdownTokens.finalTotalFontWeight};
                            text-align: right;
                            white-space: nowrap;
                        `}
                    >
                        <Money amount={finalTotal} currencySymbol={currencySymbol} />
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

/**
 * Payment Options section (SR-1544). Renders below the checkout Payment section when
 * terms-based payment choices are available.
 */
const SurPaymentBreakdown: React.FC<SurPaymentBreakdownProps> = ({
    cart,
    userHasTerms,
    userHasMgbwTerms = false,
    shippingPromotionTotal = 0,
    selectedChoice,
    onChoiceChange,
}) => {
    const paymentBreakdownTokens = checkoutPaymentInformationTokens.paymentBreakdown;
    const model = useMemo(
        () => getPaymentBreakdown(cart, userHasTerms, userHasMgbwTerms, shippingPromotionTotal),
        [cart, userHasTerms, userHasMgbwTerms, shippingPromotionTotal],
    );
    const currencySymbol = cart?.currencySymbol;

    // The customer's actual terms label (e.g. "NET 45 DAYS", "2 % 10 NET 30") - the description
    // of their terms payment method. Falls back to "Net 30" when none can be resolved. Shown on
    // the On Terms card and in the terms copy instead of a hard-coded "Net 30".
    const termsLabel = useMemo(() => {
        const termsMethod = cart?.paymentOptions?.paymentMethods?.find(isTermsPaymentMethod);
        return termsMethod?.description || termsMethod?.name || "Net 30";
    }, [cart]);

    // Keep the selected choice valid for the current scenario; default when it drifts.
    useEffect(() => {
        if (!model.availableChoices.includes(selectedChoice)) {
            onChoiceChange(model.defaultChoice);
        }
    }, [model, selectedChoice, onChoiceChange]);

    const activeChoice = model.availableChoices.includes(selectedChoice) ? selectedChoice : model.defaultChoice;
    const splitTotals = getSplitTotals(model, activeChoice);
    const [expandedChoice, setExpandedChoice] = useState<PaymentSplitChoice | null>(null);

    useEffect(() => {
        if (expandedChoice && expandedChoice !== activeChoice) {
            setExpandedChoice(null);
        }
    }, [activeChoice, expandedChoice]);

    // Simple "total charged today" layout (no terms available, or MGBW-only cart).
    if (model.scenario === "allCreditCard") {
        return (
            <SectionBox>
                <StyledWrapper
                    css={css`
                        display: flex;
                        align-items: baseline;
                        justify-content: space-between;
                    `}
                >
                    <Typography
                        css={css`
                            font-size: 16px;
                            font-weight: 700;
                            color: ${COLORS.checkout.text.primary};
                        `}
                    >
                        {translate("Total Charged Today")}
                    </Typography>
                    <Typography
                        css={css`
                            font-size: 22px;
                            font-weight: 700;
                            color: ${COLORS.checkout.text.primary};
                        `}
                    >
                        <Money amount={model.grandTotal} currencySymbol={currencySymbol} />
                    </Typography>
                </StyledWrapper>
                <Typography
                    as="p"
                    css={css`
                        margin-top: 6px;
                        font-size: 12px;
                        text-align: right;
                        color: ${COLORS.checkout.text.secondary};
                    `}
                >
                    {translate("All items must be paid by credit card today.")}
                </Typography>
            </SectionBox>
        );
    }

    const getEntityDetailGroups = (
        kind: PaymentDetailGroup["kind"],
        heading: string,
        tag: string,
        showTotal: boolean,
        totalLabel: string,
        showSplitLabels: boolean,
    ): PaymentDetailGroup[] => {
        if (!showSplitLabels) {
            return [
                {
                    kind,
                    heading,
                    tag,
                    amounts: combineGroups(model.mgbw, model.suryaGv),
                    showTotal,
                    totalLabel,
                },
            ];
        }

        return [
            ...(model.hasMgbw
                ? [
                      {
                          kind,
                          heading,
                          tag,
                          label: MGBW_LABEL,
                          amounts: model.mgbw,
                          showTotal,
                          totalLabel,
                      },
                  ]
                : []),
            ...(model.hasSuryaGv
                ? [
                      {
                          kind,
                          heading,
                          tag,
                                                      label: OTHER_ITEMS_LABEL,
                          amounts: model.suryaGv,
                          showTotal,
                          totalLabel,
                      },
                  ]
                : []),
        ];
    };

    const getOptionView = (choice: PaymentSplitChoice): PaymentOptionView => {
        if (choice === "prepayMgbwOnly") {
            return {
                title: "Prepay Mitchell Gold + Bob Williams items only",
                subtitle: "Pay the remaining balance for all other items on terms.",
                headlineAmount: model.mgbw.total,
                summaries: [
                    {
                        kind: "chargedToday",
                        heading: "Charged Today",
                        tag: "Credit Card",
                        label: MGBW_LABEL,
                        amount: model.mgbw.total,
                    },
                    {
                        kind: "onTerms",
                        heading: "On Terms (Pay Later)",
                        tag: termsLabel,
                        label: OTHER_ITEMS_LABEL,
                        amount: model.suryaGv.total,
                    },
                ],
                detailGroups: [
                    {
                        kind: "chargedToday",
                        heading: "Charged Today",
                        tag: "Credit Card",
                        label: MGBW_LABEL,
                        amounts: model.mgbw,
                        showTotal: true,
                        totalLabel: "Total Charged Today",
                    },
                    {
                        kind: "onTerms",
                        heading: "On Terms (Pay Later)",
                        tag: termsLabel,
                        label: OTHER_ITEMS_LABEL,
                        amounts: model.suryaGv,
                        showTotal: true,
                        totalLabel: "Total on Terms",
                    },
                ],
            };
        }

        if (choice === "payAllOnTerms") {
            return {
                title: `Pay the entire order on terms (${termsLabel})`,
                subtitle: `Charged Today: $0.00. The full order will be billed to your account on ${termsLabel} terms.`,
                headlineAmount: 0,
                summaries: [
                    {
                        kind: "onTerms",
                        heading: "On Terms (Pay Later)",
                        tag: termsLabel,
                        label: ENTIRE_ORDER_LABEL,
                        amount: model.grandTotal,
                    },
                ],
                detailGroups: getEntityDetailGroups(
                    "onTerms",
                    "On Terms (Pay Later)",
                    termsLabel,
                    false,
                    "",
                    false,
                ),
                finalTotalLabel: "Total on Terms",
                finalTotal: model.grandTotal,
            };
        }

        const paidGroupLabel =
            model.hasMgbw && model.hasSuryaGv
                ? `${MGBW_LABEL} and ${OTHER_ITEMS_LABEL}`
                : ENTIRE_ORDER_LABEL;

        return {
            title:
                model.scenario === "mgbwPrepaySplit"
                    ? `Prepay all items (${MGBW_LABEL} and ${OTHER_ITEMS_LABEL})`
                    : "Pay the entire order with credit card",
            subtitle: "No remaining balance to pay.",
            headlineAmount: model.grandTotal,
            summaries: [
                {
                    kind: "chargedToday",
                    heading: "Charged Today",
                    tag: "Credit Card",
                    label: paidGroupLabel,
                    amount: model.grandTotal,
                },
            ],
            detailGroups: getEntityDetailGroups(
                "chargedToday",
                "Charged Today",
                "Credit Card",
                false,
                "",
                model.hasMgbw && model.hasSuryaGv,
            ),
            finalTotalLabel: "Total Charged Today",
            finalTotal: model.grandTotal,
        };
    };

    const renderOptionLabel = (view: PaymentOptionView) => {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    min-width: 0;
                    gap: ${paymentBreakdownTokens.optionLabelGap};
                    align-items: flex-start;
                    flex-wrap: wrap;
                `}
            >
                <StyledWrapper css={css`min-width: 0; flex: 1 1 ${paymentBreakdownTokens.optionTextMinWidth};`}>
                    <Typography
                        css={css`
                            color: ${COLORS.checkout.text.primary};
                            font-weight: ${paymentBreakdownTokens.optionTitleFontWeight};
                            line-height: ${paymentBreakdownTokens.optionTitleLineHeight};
                        `}
                    >
                        {translate(view.title)}
                    </Typography>
                    <Typography
                        as="p"
                        css={css`
                            margin: 0;
                            color: ${COLORS.checkout.text.secondary};
                            font-size: ${paymentBreakdownTokens.optionSubtitleFontSize};
                            line-height: ${paymentBreakdownTokens.optionSubtitleLineHeight};
                        `}
                    >
                        {translate(view.subtitle)}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        flex: 0 0 100%;
                        text-align: left;
                        white-space: nowrap;

                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    flex: 0 0 auto;
                                    text-align: right;
                                `,
                                null,
                                null,
                            ], mediaOptions.min)}
                    `}
                >
                    <Typography
                        css={css`
                            color: ${COLORS.checkout.text.primary};
                            font-size: ${paymentBreakdownTokens.optionAmountMobileFontSize};
                            font-weight: ${paymentBreakdownTokens.optionAmountFontWeight};
                            line-height: ${paymentBreakdownTokens.optionAmountLineHeight};

                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(theme, [
                                    null,
                                    null,
                                    css`
                                        font-size: ${paymentBreakdownTokens.optionAmountFontSize};
                                    `,
                                    null,
                                    null,
                                ], mediaOptions.min)}
                        `}
                    >
                        <Money amount={view.headlineAmount} currencySymbol={currencySymbol} />
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    return (
        <SectionBox>
            <Typography
                as="p"
                css={css`
                    font-weight: 700;
                    color: ${COLORS.checkout.text.primary};
                    margin-bottom: 8px;
                `}
            >
                {translate("How would you like to pay?")}
            </Typography>
            <RadioGroup
                value={activeChoice}
                onChangeHandler={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setExpandedChoice(null);
                    onChoiceChange(event.target.value as PaymentSplitChoice);
                }}
                css={css`
                    gap: ${paymentBreakdownTokens.optionGap};
                `}
            >
                {model.availableChoices.map(choice => {
                    const view = getOptionView(choice);
                    const isSelected = choice === activeChoice;
                    const isExpanded = choice === expandedChoice;
                    const toggleId = `payment-breakdown-toggle-${choice}`;
                    const panelId = `payment-breakdown-panel-${choice}`;

                    return (
                        <StyledWrapper
                            key={choice}
                            css={css`
                                overflow: hidden;
                                border: ${isSelected
                                    ? paymentBreakdownTokens.optionSelectedBorder
                                    : paymentBreakdownTokens.optionBorder};
                                border-radius: ${paymentBreakdownTokens.boxBorderRadius};
                                box-shadow: ${isSelected ? paymentBreakdownTokens.optionSelectedBoxShadow : "none"};
                            `}
                        >
                            <PaymentRadio
                                value={choice}
                                css={css`
                                    margin: 0;
                                    padding: ${paymentBreakdownTokens.optionHeaderMobilePadding};

                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(theme, [
                                            null,
                                            null,
                                            css`
                                                padding: ${paymentBreakdownTokens.optionHeaderPadding};
                                            `,
                                            null,
                                            null,
                                        ])}

                                    & > input {
                                        position: absolute;
                                    }

                                    & > label {
                                        display: flex;
                                        width: 100%;
                                        align-items: center;
                                    }

                                    & > label > div {
                                        flex: 1 1 auto;
                                        min-width: 0;
                                    }
                                `}
                            >
                                {renderOptionLabel(view)}
                            </PaymentRadio>
                            {isSelected && (
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        flex-direction: column;
                                        gap: ${paymentBreakdownTokens.selectedContentGap};
                                        padding: ${paymentBreakdownTokens.selectedContentMobilePadding};

                                        ${({ theme }: { theme: BaseTheme }) =>
                                            breakpointMediaQueries(theme, [
                                                null,
                                                null,
                                                css`
                                                    padding: ${paymentBreakdownTokens.selectedContentPadding};
                                                `,
                                                null,
                                                null,
                                            ])}

                                        & > .payment-breakdown-toggle {
                                            align-self: flex-start;
                                            min-height: ${paymentBreakdownTokens.disclosureMinHeight};
                                            padding: ${paymentBreakdownTokens.disclosurePadding};
                                            background: transparent;
                                            border: 0;
                                            color: ${paymentBreakdownTokens.disclosureColor};
                                            cursor: pointer;
                                            font-family: ${TYPOGRAPHY.fontFamily.base};
                                            font-size: ${paymentBreakdownTokens.disclosureFontSize};
                                            font-weight: ${paymentBreakdownTokens.disclosureFontWeight};
                                            line-height: ${paymentBreakdownTokens.disclosureLineHeight};
                                            text-decoration: underline;
                                            text-underline-offset: ${paymentBreakdownTokens.disclosureUnderlineOffset};
                                        }

                                        & > .payment-breakdown-toggle:focus-visible {
                                            outline: ${paymentBreakdownTokens.disclosureFocusOutline};
                                            outline-offset: ${paymentBreakdownTokens.disclosureFocusOutlineOffset};
                                        }
                                    `}
                                >
                                    {view.summaries
                                        .filter(summary => summary.amount > 0)
                                        .map(summary => (
                                            <CompactAllocationRow
                                                key={summary.kind}
                                                summary={summary}
                                                currencySymbol={currencySymbol}
                                            />
                                        ))}
                                    <button
                                        id={toggleId}
                                        type="button"
                                        className="payment-breakdown-toggle"
                                        aria-expanded={isExpanded}
                                        aria-controls={panelId}
                                        onClick={() => setExpandedChoice(isExpanded ? null : choice)}
                                    >
                                        {translate(isExpanded ? "Hide breakdown" : "View breakdown")}
                                    </button>
                                    <ExpandCollapseTransition
                                        in={isExpanded}
                                        keepMounted
                                        id={panelId}
                                        role="region"
                                        aria-labelledby={toggleId}
                                    >
                                        <DetailedBreakdown
                                            groups={view.detailGroups}
                                            finalTotalLabel={view.finalTotalLabel}
                                            finalTotal={view.finalTotal}
                                            currencySymbol={currencySymbol}
                                        />
                                    </ExpandCollapseTransition>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    );
                })}
            </RadioGroup>
            <StyledWrapper
                css={css`
                    padding-top: ${paymentBreakdownTokens.additionalMessagePaddingTop};
                    border-top: ${paymentBreakdownTokens.additionalMessageBorder};
                `}
            >
                <Typography
                    as="p"
                    css={css`
                        margin: 0;
                        font-size: ${paymentBreakdownTokens.additionalMessageFontSize};
                        color: ${COLORS.checkout.text.secondary};
                    `}
                >
                    {splitTotals.onTerms > 0 && splitTotals.chargedToday > 0
                        ? translate(`Charged Today by credit card; the remaining balance is payable on ${termsLabel} terms.`)
                        : splitTotals.onTerms > 0
                        ? translate(`The entire order is placed on your account (${termsLabel} terms).`)
                        : translate("The entire order is charged today by credit card.")}
                </Typography>
            </StyledWrapper>
        </SectionBox>
    );
};

export default SurPaymentBreakdown;
